import { type ReactElement, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useAuthStore } from '@/entities/user';
import { useSignOut } from '@/features/auth';
import { HOME_PATH } from '@/shared/config';
import { useScrolledAbove } from '@/shared/lib';
import { BrandLogo, cn } from '@/shared/ui';
import { SiteHeaderAnnouncement } from './site-header-announcement';
import { SiteHeaderGnb } from './site-header-gnb';
import { SiteHeaderIntro } from './site-header-intro';
import { SiteHeaderMemberNav } from './site-header-member-nav';
import { SiteHeaderMenu } from './site-header-menu';
import { useHeaderIntro } from './use-header-intro';

const MENU_ID = 'site-header-menu';

interface SiteHeaderProps {
  /**
   * bar = HOME 외 모든 고객 화면(기본) — 위에 붙는 흰 줄(작은 로고·GNB·회원 메뉴)과 아래 얇은 선.
   * overlay = HOME — 맨 위 공지 띠 아래, 화면 높이 히어로 사진 위에 흰 로고·메뉴를 얹는다.
   */
  readonly variant?: 'bar' | 'overlay';
  /** overlay 전용 — 화면 맨 위에 고정한 로고 줄이 히어로 사진을 지나 흰 본문 위에 있는지(흰 바탕·검정 글자). */
  readonly isOverContent?: boolean;
}

/**
 * 앱 전역 헤더 — 왼쪽 작은 로고(홈 링크)와 GNB, 오른쪽 회원 메뉴(HOME 은 사진 위 큰 로고 아래 한 줄에 GNB).
 * 기본 흰 줄은 화면 위에 붙어(sticky) 스크롤해도 남고, HOME 은 사진을 지나면 같은 모양의 고정 줄로 바뀐다.
 * 좁은 화면(xl 미만)에서는 MENU 버튼이 GNB·회원 메뉴를 펼친다. GNB 는 shared/config/gnb.ts 가 단일 원천이다.
 */
export const SiteHeader = ({
  variant = 'bar',
  isOverContent = false,
}: SiteHeaderProps): ReactElement => {
  const user = useAuthStore((state) => state.user);
  const { signOut } = useSignOut();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // 사진 위에 얹은 로고·메뉴가 스크롤로 화면 위로 지나가면 화면 맨 위에 고정한 로고 줄로 바꾼다.
  const overlayRef = useRef<HTMLDivElement>(null);
  const isCondensed = useScrolledAbove(overlayRef);
  // HOME 첫 진입 — 흑연 막 위 로고가 떠오른 뒤 막이 걷힌다(세션당 한 번).
  const introPhase = useHeaderIntro(variant === 'overlay');

  // 펼친 메뉴는 Escape 로 닫는다.
  useEffect(() => {
    if (!isMenuOpen) {
      return undefined;
    }

    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [isMenuOpen]);

  const menuButton = (
    <button
      type="button"
      aria-expanded={isMenuOpen}
      aria-controls={MENU_ID}
      onClick={() => setIsMenuOpen((isOpen) => !isOpen)}
      className="text-xs font-semibold tracking-[0.08em] xl:hidden"
    >
      {isMenuOpen ? 'CLOSE' : 'MENU'}
    </button>
  );

  const menu = isMenuOpen ? (
    <SiteHeaderMenu
      id={MENU_ID}
      isSignedIn={Boolean(user)}
      onSignOut={() => void signOut()}
      onNavigate={() => setIsMenuOpen(false)}
    />
  ) : null;

  if (variant === 'overlay') {
    return (
      <header className="relative z-30">
        <SiteHeaderAnnouncement />
        {introPhase !== 'done' ? <SiteHeaderIntro phase={introPhase} /> : null}

        {/* 사진 위 로고·메뉴 — 공지 띠 바로 아래, 히어로 사진 위에 투명하게 얹혀 사진과 함께 스크롤된다.
            첫 진입 막이 걷히는 동안 살짝 늦게 떠오르며 나타난다. */}
        <div
          ref={overlayRef}
          inert={isCondensed}
          className={cn(
            'absolute inset-x-0 top-full px-5 pt-6 text-white transition-[opacity,translate] duration-700 ease-out sm:px-8 sm:pt-8 xl:px-14 motion-reduce:transition-none',
            introPhase === 'logo'
              ? 'translate-y-2 opacity-0'
              : 'translate-y-0 opacity-100 delay-500',
          )}
        >
          <div className="flex items-start justify-between gap-6">
            {/* 링크 이름은 로고 SVG 의 aria-label(SuperStructure)이 준다. */}
            <Link to={HOME_PATH} className="block">
              <BrandLogo className="h-9 sm:h-11" />
            </Link>
            <SiteHeaderMemberNav
              isSignedIn={Boolean(user)}
              onSignOut={() => void signOut()}
              listClassName="gap-6 text-[13px]"
              tone="inverse"
            />
            <div className="pt-2 xl:hidden">{menuButton}</div>
          </div>
          <div className="mt-6 hidden xl:block">
            <SiteHeaderGnb tone="inverse" listClassName="gap-8 text-[15px]" />
          </div>

          {isCondensed ? null : menu}
        </div>

        {/* 고정 로고 줄 — 사진 위 로고·메뉴가 화면 위로 지나가면 나타난다. 히어로 사진 위에서는 투명 바탕에
            흰 글자, 사진 구간을 지나면 흰 바탕에 검정 글자. 숨어 있는 동안에는 조작도 막는다. */}
        <div
          inert={!isCondensed}
          className={cn(
            'fixed inset-x-0 top-0 transition-[opacity,background-color,color] duration-300 ease-out motion-reduce:transition-none',
            isCondensed ? 'opacity-100' : 'pointer-events-none opacity-0',
            isOverContent
              ? 'border-b border-foreground/10 bg-background text-foreground'
              : 'text-white',
          )}
        >
          <div className="flex h-16 items-center justify-between gap-6 px-5 sm:h-20 sm:px-8 xl:px-14">
            <div className="flex items-center gap-12">
              <Link to={HOME_PATH} className="block">
                <BrandLogo className="h-7 sm:h-8" />
              </Link>
              <SiteHeaderGnb
                tone={isOverContent ? 'default' : 'inverse'}
                listClassName="gap-8 text-[15px]"
              />
            </div>
            <SiteHeaderMemberNav
              isSignedIn={Boolean(user)}
              onSignOut={() => void signOut()}
              listClassName="gap-6 text-[13px]"
              tone={isOverContent ? 'default' : 'inverse'}
            />
            <div className="xl:hidden">{menuButton}</div>
          </div>

          {isCondensed ? menu : null}
        </div>
      </header>
    );
  }

  return (
    // 화면 흐름 안에 서서 위로 붙는다(sticky) — 고정(fixed)과 달리 빈 자리를 따로 남길 필요가 없다.
    <header className="sticky top-0 z-30 border-b border-foreground/10 bg-background text-foreground">
      {/* HOME 고정 로고 줄이 흰 본문 위에 있을 때와 같은 배치 — 화면을 옮겨도 머리가 바뀌지 않게. */}
      <div className="flex h-16 items-center justify-between gap-6 px-5 sm:h-20 sm:px-8 xl:px-14">
        <div className="flex items-center gap-12">
          {/* 링크 이름은 로고 SVG 의 aria-label(SuperStructure)이 준다. */}
          <Link to={HOME_PATH} className="block">
            <BrandLogo className="h-7 sm:h-8" />
          </Link>
          <SiteHeaderGnb listClassName="gap-8 text-[15px]" />
        </div>
        <SiteHeaderMemberNav
          isSignedIn={Boolean(user)}
          onSignOut={() => void signOut()}
          listClassName="gap-6 text-[13px]"
        />
        <div className="xl:hidden">{menuButton}</div>
      </div>

      {menu}
    </header>
  );
};
